import { Tag as CircleTag } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import CheckBox from 'modules/core/components/Inputs/CheckBox'
import {
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'modules/core/components/Table'
import type { WithMultiSelectStateChildrenProps } from 'modules/core/components/WithMultiSelectState'
import i18n from 'modules/core/lib/i18n'

import getAttributeHeader from 'modules/employees/pages/ImportContractsPage/utils/getAttributeHeader'

import BasicCellContent from './BasicCellContent'
import CustomFieldCellContent from './CustomFieldCellContent'
import { ImportedContract } from './types'

type WithMultiSelectProps = WithMultiSelectStateChildrenProps<ImportedContract>

interface Props {
  contracts: ImportedContract[]
  selected: ImportedContract[]
  attributes: string[]
  customFields: string[]
  onToggleSelectedItem: WithMultiSelectProps['onToggleSelectedItem']
  onDeselectAll: () => void
  onSelectAll: () => void
}

const Content = ({
  contracts,
  selected,
  attributes,
  customFields,
  onToggleSelectedItem,
  onDeselectAll,
  onSelectAll,
}: Props) => {
  const onToggleItem = (contract: ImportedContract) => {
    if (contract.status !== 'errored') {
      onToggleSelectedItem(contract)
    }
  }

  const onClickAll = () => {
    if (contracts.length === selected.length) {
      onDeselectAll()
    } else {
      onSelectAll()
    }
  }

  return (
    <>
      <TableHead>
        <TableRow>
          <TableHeadCell key="check">
            <CheckBox
              checked={selected.length === contracts.length}
              disabled={contracts.some((contract) => contract.status === 'errored')}
              onClick={onClickAll}
            />
          </TableHeadCell>
          <TableHeadCell key="type">{i18n.t('import.table.headers.type')}</TableHeadCell>

          {attributes.map((attribute) => (
            <TableHeadCell key={attribute}>{getAttributeHeader(attribute)}</TableHeadCell>
          ))}
          {customFields.map((attribute: string, index: number) => (
            <TableHeadCell key={index}>{attribute}</TableHeadCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {contracts.map((contract) => {
          const checked = selected.includes(contract)

          const type = contract.type === 'new' ? CircleTag.Types.lime : CircleTag.Types.champagne

          const extraProps: Record<string, string> =
            contract.status === 'errored'
              ? {
                  color: 'wrong',
                }
              : {}

          return (
            <TableRow key={contract.id} {...extraProps}>
              <TableBodyCell key="check" centered>
                <CheckBox
                  checked={checked}
                  disabled={contract.status === 'errored'}
                  onClick={() => onToggleItem(contract)}
                />
              </TableBodyCell>
              <TableBodyCell key="type" centered>
                <CircleTag capitalize inline type={type}>
                  {contract.type}
                </CircleTag>
              </TableBodyCell>

              {attributes.map((attribute) => (
                <TableBodyCell key={attribute}>
                  <BasicCellContent contract={contract} attribute={attribute} />
                </TableBodyCell>
              ))}

              {customFields.map((attribute: string, index: number) => (
                <TableBodyCell key={index}>
                  <CustomFieldCellContent contract={contract} index={index} attribute={attribute} />
                </TableBodyCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </>
  )
}

export default observer(Content)
